import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";

import {
    Box,
    Button,
    Divider,
    TextField,
    Grid,
    Card,
    styled,
    Typography,
    IconButton,
    List,
    ListItem,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Tooltip,
    FormControlLabel,
    InputAdornment,
    FormGroup,
    Checkbox,
} from '@mui/material';

import _ from "lodash";

import { AgGridReact } from 'ag-grid-react';
import {
    ClientSideRowModelModule,
    ValidationModule,
    TextEditorModule,
    NumberEditorModule,
    SelectEditorModule,
    CellStyleModule,
    RowSelectionModule,
    RowStyleModule,
} from 'ag-grid-community';

import { green, grey } from "@mui/material/colors";

import * as Yup from "yup";

import { useFormik } from 'formik';

import { CommonIcons, icons } from "../../../../../../components/common/CommonIcons";
import { useSystemPrompt } from "../../../../../../hooks/useSystemPrompt";
import { useSystemRoutes } from '../../../../../../hooks/useSystemRoutes';
import { useAuthAccess } from "../../../../../../hooks/useAuthAccess";

import { useTranslations } from "../../../../../../hooks/useTranslations";

import { calculateProductItemsSummary, calculateProductItemSummary } from "../../../../utils/formatDimension";

import {
    useProduct,
    initialItemValues,
    validateItemSchema as validateItemSchema1,
} from "../../../../hooks/model/useProduct"

import {
    defaultUom,
    getUniqueItemCode,
    itemUoms,
} from "../../../../hooks/model/useProductItem"

import { mergeSchema } from "../../../../../../utils/dataSchema"

import { formatRoundValue } from "../../../../utils/formatNumber"

const maxItemLength = 100

export const validateItemSchema2 = Yup.object().shape({
    customUomName: Yup
        .string()
        .required("Required")
})

const validateItemSchema = mergeSchema(validateItemSchema1, validateItemSchema2)

export const validationSchema = Yup.object({
    items: Yup.array().of(validateItemSchema),
})

export const initialValues = {
    items: [
        initialItemValues
    ],
    isDeleteFirstRowOfSavedItems: false
}

const StyledBoxSummary = styled(Box)(({ theme }) => ({
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 3,
    paddingBottom: 3,
    // borderWidth: 1,
    // borderStyle: "solid",
    backgroundColor: green[800],
    marginRight: 5,
    textAlign: 'center',
    minWidth: 100,
    borderRadius: 10,
}));

const RenderDatagrid = ({ t, currentLangId, gridRef, values, onCellValueChanged }) => {

    let columnDefs = []

    if (currentLangId === 'th-TH') {

        columnDefs = [
            {
                headerName: '',
                field: 'checkbox',
                width: 40,
                cellRenderer: 'agCheckboxCellRenderer',
                headerCheckboxSelection: true,
                checkboxSelection: true,
                // headerCheckboxSelectionFilteredOnly: true,
                // suppressMenu: true,
                resizable: false,
                pinned: 'left',
            },
            { valueGetter: 'node.rowIndex + 1', headerName: `${t("no")}`, cellStyle: { textAlign: 'center', fontSize: 12 }, editable: false, width: 40 },
            { field: 'remark', headerName: `${t("model")}`, editable: true, width: 180 },
            { field: 'description', headerName: `${t("itemName")}`, editable: true, width: 150 },
            { field: 'handleQty', headerName: `${t("handleQty")}`, cellStyle: { textAlign: 'center' }, editable: true, width: 100 },
            { field: 'uomPrice', headerName: `${t("unitPrice")}`, editable: true, width: 85 },
            { field: 'handleQtyPerPackage', headerName: `${t("pcsCtn")}`, cellStyle: { textAlign: 'center' }, editable: true, width: 110 },
            { field: 'length', headerName: `${t("length")}`, cellStyle: { textAlign: 'center' }, editable: true, width: 70 },
            { field: 'width', headerName: `${t("width")}`, cellStyle: { textAlign: 'center' }, editable: true, width: 100 },
            { field: 'height', headerName: `${t("height")}`, cellStyle: { textAlign: 'center' }, editable: true, width: 90 },
            { field: 'qty', headerName: `${t("packUnit")}`, cellStyle: { textAlign: 'center' }, editable: true, width: 100 },
            { field: 'weight', headerName: `${t("weight")}`, cellStyle: { textAlign: 'center' }, editable: true, width: 80 },
            {
                field: 'customUomName', headerName: `${t("uom")}`, editable: true,
                cellEditor: 'agSelectCellEditor',
                cellEditorParams: {
                    // values: ['Active', 'Inactive', 'Pending'], // dropdown options
                    values: itemUoms.map((option) => {
                        return `${t(option.display)}`
                    }),
                },
                width: 90
            },
            { field: 'summaryTotalValue', headerName: `${t("tValue")}`, cellStyle: { textAlign: 'left', fontSize: 13 }, editable: false, width: 100 },
            { field: 'summaryCbm', headerName: `${t("cbm")}`, cellStyle: { textAlign: 'left', fontSize: 13 }, editable: false, width: 90 },
            { field: 'summaryTotalWeight', headerName: `${t("tWeight")}`, cellStyle: { textAlign: 'left', fontSize: 13 }, editable: false, width: 100 },
        ]

    } else { // other lang than th

        columnDefs = [
            {
                headerName: '',
                field: 'checkbox',
                width: 40,
                cellRenderer: 'agCheckboxCellRenderer',
                headerCheckboxSelection: true,
                checkboxSelection: true,
                // headerCheckboxSelectionFilteredOnly: true,
                // suppressMenu: true,
                resizable: false,
                pinned: 'left',
            },
            // {
            //     field: 'id', headerName: `${t("id")}`, cellStyle: { textAlign: 'center' }, editable: false, width: 60
            // },
            { valueGetter: 'node.rowIndex + 1', headerName: `${t("no")}`, cellStyle: { textAlign: 'center', fontSize: 12 }, editable: false, width: 50 },
            { field: 'remark', headerName: `${t("model")}`, editable: true, width: 180 },
            { field: 'description', headerName: `${t("itemName")}`, editable: true, width: 150 },
            { field: 'handleQty', headerName: `${t("handleQty")}`, cellStyle: { textAlign: 'center' }, editable: true, width: 110 },
            { field: 'uomPrice', headerName: `${t("unitPrice")}`, editable: true, width: 85 },
            { field: 'handleQtyPerPackage', headerName: `${t("pcsCtn")}`, cellStyle: { textAlign: 'center' }, editable: true, width: 90 },
            { field: 'length', headerName: `${t("length")}`, cellStyle: { textAlign: 'center' }, editable: true, width: 80 },
            { field: 'width', headerName: `${t("width")}`, cellStyle: { textAlign: 'center' }, editable: true, width: 80 },
            { field: 'height', headerName: `${t("height")}`, cellStyle: { textAlign: 'center' }, editable: true, width: 80 },
            { field: 'qty', headerName: `${t("packUnit")}`, cellStyle: { textAlign: 'center' }, editable: true, width: 100 },
            { field: 'weight', headerName: `${t("weight")}`, cellStyle: { textAlign: 'center' }, editable: true, width: 80 },
            {
                field: 'customUomName', headerName: `${t("uom")}`, editable: true,
                cellEditor: 'agSelectCellEditor',
                cellEditorParams: {
                    // values: ['Active', 'Inactive', 'Pending'], // dropdown options
                    values: itemUoms.map((option) => {
                        // return option.display
                        return `${t(option.display)}`
                    }),
                },
                width: 90
            },
            { field: 'summaryTotalValue', headerName: `${t("tValue")}`, cellStyle: { textAlign: 'left', fontSize: 13 }, editable: false, width: 100 },
            { field: 'summaryCbm', headerName: `${t("cbm")}`, cellStyle: { textAlign: 'left', fontSize: 13 }, editable: false, width: 90 },
            { field: 'summaryTotalWeight', headerName: `${t("tWeight")}`, cellStyle: { textAlign: 'left', fontSize: 13 }, editable: false, width: 100 },
        ]
    }

    const defaultColDef = useMemo(() => ({
        // flex: 1,
        // minWidth: 100,
        sortable: false,
        resizable: true,
        enableManagedDrag: false,
        headerClass: 'custom-header'
    }), []);

    return (
        <Box>
            <Box
                className="ag-theme-alpine"
                sx={{
                    width: '100%',
                    height: '100%',
                    // '& .ag-theme-alpine .ag-cell, .ag-theme-alpine .ag-header-cell': {
                    //     border: '1px solid #dcdcdc',  // Full border for cells and header
                    // },
                    // '& .ag-theme-alpine .ag-row': {
                    //     borderBottom: '1px solid #dcdcdc',  // Border between rows
                    // },
                    '.ag-cell': {
                        // borderLeft: '1px solid #dcdcdc',  // Left border for cells
                        borderRight: '1px solid #dcdcdc', // Right border for cells
                        // borderTop: '1px solid #dcdcdc', // Right border for cells
                        borderBottom: '1px solid #dcdcdc', // Right border for cells
                    },
                    // '& .ag-theme-alpine .ag-header-cell': {
                    //     borderLeft: '1px solid #dcdcdc',  // Left border for headers
                    //     borderRight: '1px solid #dcdcdc', // Right border for headers
                    // },
                    '.custom-header': {
                        backgroundColor: '#004085',
                        color: grey[100],
                        fontWeight: 'bold',
                        fontSize: 15,
                        textTransform: 'uppercase',
                    }
                }}
            >

                <AgGridReact
                    ref={gridRef}

                    modules={[
                        ClientSideRowModelModule,
                        ValidationModule,
                        TextEditorModule,
                        NumberEditorModule,
                        SelectEditorModule,
                        CellStyleModule,
                        RowSelectionModule,
                        RowStyleModule,
                    ]}
                    rowData={values}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    // editType="fullRow"
                    singleClickEdit={Boolean(true)}
                    domLayout='autoHeight'
                    rowHeight={35}  // Reduce the row height
                    headerHeight={30} // sets height in pixels
                    onCellValueChanged={onCellValueChanged}
                    rowSelection="multiple"
                    suppressMovableColumns={Boolean(true)}
                    getRowStyle={(params) => ({
                        fontSize: 18,
                        backgroundColor: params.node.rowIndex % 2 === 0 ? `#ffffff` : grey[200]
                    })}
                />
            </Box>
        </Box>
    )
}

const RenderForm = ({
    gridRef,
    currentLangId,
    t, formik, summaryItems, handleAddRow, handleRemoveRows, onCellValueChanged,
    allowDeleteFirstRowAfterSave = false,
    onCalculateProductItem,
    printPackQtyValue,
    getRoute,
    availableLocations
}) => {

    const { values, errors, handleChange } = formik

    const [insertRowsCount, setInsertRowsCount] = useState(1)

    let summary = summaryItems
    let noVal = 0.0000

    // calculate balance left the max row allowed
    let leftRowAllowed = Number(maxItemLength) - Number(values.items.length - 1)

    let valueInsertRowsNo = 0

    if (insertRowsCount <= leftRowAllowed) {
        valueInsertRowsNo = Number(insertRowsCount)
    } else {
        valueInsertRowsNo = leftRowAllowed
    }

    let printPackQty = 0

    if (printPackQtyValue) {
        printPackQty = printPackQtyValue
    }

    return (

        <form onSubmit={formik.handleSubmit}>
            {/* {JSON.stringify(values)} */}
            <Box
                sx={{
                    mt: 0,
                    mb: 2,
                    mx: 2
                }}
            >
                <Grid container spacing={0}>
                    <Grid
                        item xs={10} md={10} sm={10}
                        display={"flex"} flexDirection={"row"} justifyContent={"flex-start"} alignItems={"flex-end"}
                    >
                        <StyledBoxSummary>
                            <Typography sx={{ fontSize: 11, color: grey[100] }}>
                                {`${t('printPackQty')} :`}
                            </Typography>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: grey[100] }}>
                                {printPackQty}
                            </Typography>
                        </StyledBoxSummary>

                        <StyledBoxSummary>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    color: grey[100]
                                }}
                            >
                                {`${t('totalPackQty')} : `}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    color: grey[100]
                                }}
                            >
                                {/* {`${summary?.qty}`} */}
                                {(summary?.qty) ? summary?.qty : 0}
                            </Typography>
                        </StyledBoxSummary>

                        <StyledBoxSummary>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    color: grey[100]
                                }}
                            >
                                {`${t('totalValues')} : `}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    color: grey[100]
                                }}
                            >
                                {/* {` ${summary?.value}`} */}
                                {(summary?.value) ? summary?.value : parseFloat(noVal).toFixed(4)}
                            </Typography>
                        </StyledBoxSummary>


                        <StyledBoxSummary>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    color: grey[100]
                                }}
                            >
                                {`${t('totalWeight')} : `}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    color: grey[100]
                                }}
                            >
                                {/* {` ${summary?.weight}`} */}
                                {(summary?.weight) ? summary?.weight : parseFloat(noVal).toFixed(4)}
                            </Typography>
                        </StyledBoxSummary>

                        <StyledBoxSummary>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    color: grey[100]
                                }}
                            >
                                {`${t('totalCbm')} : `}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    color: grey[100]
                                }}
                            >
                                {/* {` ${itemValues?.totalCbm ? itemValues.totalCbm : 0}`} */}
                                {/* {` ${summary?.cbm}`} */}
                                {(summary?.cbm) ? summary?.cbm : parseFloat(noVal).toFixed(4)}
                            </Typography>
                        </StyledBoxSummary>

                        <Button
                            variant="contained"
                            color="info"
                            sx={{ ml: 1, alignSelf: 'center' }}
                            onClick={() => onCalculateProductItem()}
                        >
                            {t('calculate')}
                        </Button>
                    </Grid>
                    <Grid item xs={2} md={2} sm={2} display={"flex"} flexDirection={"row"} justifyContent={"flex-end"} alignItems={"center"}>

                        <FormControl
                            size="small"
                        // error={Boolean(errors?.searchBarcode)}
                        >
                            <TextField
                                // autoFocus
                                // required
                                // margin="dense"
                                size='small'
                                id="insertRows"
                                name="insertRows"
                                label={t("insertRows")}
                                type="text"
                                value={
                                    valueInsertRowsNo
                                }
                                onChange={e => setInsertRowsCount(e.target.value)}
                                fullWidth
                                // variant="standard"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Button
                                                type="button"
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                onClick={() => {
                                                    handleAddRow({ rowCount: insertRowsCount })
                                                }}
                                            >
                                                <CommonIcons name={icons.plus} />
                                            </Button>
                                        </InputAdornment>
                                    ),
                                }}
                                onFocus={event => {
                                    event.target.select();
                                }}
                            // sx={{
                            //     width: 400
                            // }}
                            />
                            {/* <FormHelperText>{errors?.searchBarcode}</FormHelperText> */}
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>

            {/* {JSON.stringify(itemUoms)}

            {JSON.stringify(initialItemValues)} */}

            <RenderDatagrid
                currentLangId={currentLangId}
                gridRef={gridRef}
                t={t}
                values={values.items}
                handleRemoveRows={handleRemoveRows}
                onCellValueChanged={onCellValueChanged}
                getRoute={getRoute}
                availableLocations={availableLocations}
            />

            <Grid container spacing={1} sx={{ pl: 2, pr: 2, mt: 1 }}>
                <Grid item md={6} textAlign={"left"}>
                    <Button
                        type="button"
                        // fullWidth
                        variant="contained"
                        color="error"
                        sx={{
                            // ml: 3,
                            // mb: 2
                        }}
                        onClick={handleRemoveRows}
                    >
                        {t('remove')}
                    </Button>
                </Grid>
                <Grid item md={6} textAlign={"right"}>
                    <Box
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"flex-end"}
                        alignItems={"center"}
                    >
                        <Button
                            variant="contained"
                            color="info"
                            sx={{ mr: 2 }}
                            onClick={() => onCalculateProductItem()}
                        >
                            {t('calculate')}
                        </Button>

                        {
                            allowDeleteFirstRowAfterSave === true &&
                            (
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values?.isDeleteFirstRowOfSavedItems}
                                                onChange={handleChange}
                                                id={"isDeleteFirstRowOfSavedItems"}
                                                name={"isDeleteFirstRowOfSavedItems"}
                                            />
                                        }
                                        label={`${t("deleteFirstRowOfSavedItems")}`}
                                    />
                                </FormGroup>
                            )
                        }
                        <Button
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                // mt: 3,
                                // mb: 2
                            }}
                        >
                            {t('saveChanges')}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </form >

    )
}

export default function AddProductItemsV2({
    routeType,
    currentData,
    productId,
    productIndex,
    courierTrackingNo,
    onSaveSuccess = () => { },
    allowDeleteFirstRowAfterSave = false,
    printPackQtyValue
}) {

    const gridRef = useRef();

    const { promptMessage } = useSystemPrompt();

    const { getRoute } = useSystemRoutes(routeType);
    const { getAvailableLocations } = useAuthAccess();
    const availableLocations = getAvailableLocations()

    const { t, getCurrentLanguageId } = useTranslations({ ns: ["_@products", "_prompt", "_@repositories", "_@parcel", "common", "_common"] })

    const currentLangId = getCurrentLanguageId()

    const [summaryItems, setSummaryItems] = useState({})

    const onSubmit = async (currentValues) => {

        let { items, totalSummary } = calculateProductItemsSummary(currentValues.items)

        currentValues.items = items

        if (onSaveSuccess) {
            onSaveSuccess(
                currentValues,
                productIndex,
                totalSummary,
                currentValues?.isDeleteFirstRowOfSavedItems, // delete first row of item once added new items
            )
        }

        promptMessage(true, `Entry #${(productIndex + 1)} : Items (${currentValues.items.length}) has been saved`)

        return true
    }

    const generateRowId = () => {
        return _.uniqueId("")
    }

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    // load the first time
    useEffect(() => {

        if (currentData.length <= 0) {

            let _data = [{
                ...initialItemValues,
                id: generateRowId(),
                uomId: defaultUom.id,
                // customUomName: defaultUom.uomName,
                customUomName: t(defaultUom.value),
                code: getUniqueItemCode({ courierTrackingNo, productId: 'TEMP' }),
                summaryCbm: formatRoundValue(0, 4).toFixed(4),
                summaryTotalValue: formatRoundValue(parseFloat(0), 2).toFixed(2),
                summaryTotalCbm: formatRoundValue(parseFloat(0), 4).toFixed(4),
                summaryTotalWeight: formatRoundValue(parseFloat(0), 4).toFixed(4),
            }]

            setFieldValue(`items`, _data)

        } else {
            setFieldValue(`items`, currentData)
        }

    }, [])

    // auto calculate the grand summary
    // useEffect(() => {

    //     let currentItems = values.items

    //     let { totalSummary } = calculateProductItemsSummary(currentItems)

    //     setSummaryItems(totalSummary)

    // }, [values.items])

    const onRowDataChange = (updatedRow) => {

        // let _updatedRow = calculateProductItemSummary(updatedRow)
        let _updatedRow = updatedRow
        setFieldValue(`items`, values.items.map(row => (row.id === _updatedRow.id ? _updatedRow : row)))

    };

    const onCellValueChanged = useCallback((event) => {
        onRowDataChange(event.data);
    }, [onRowDataChange]);

    const handleRemoveRows = () => {
        const selectedNodes = gridRef.current.api.getSelectedNodes();
        const selectedIds = selectedNodes.map((node) => node.data.id);
        const newRows = values.items.filter(row => !selectedIds.includes(row.id));
        setFieldValue(`items`, newRows);
    };

    const onCalculateProductItem = () => {

        let { totalSummary } = calculateProductItemsSummary(values.items)

        let items = []

        values.items.forEach(i => {
            items.push(
                calculateProductItemSummary(i)
            )
        })

        // console.log('total', totalSummary)
        // console.log('items', items, values.items)

        setSummaryItems(totalSummary)
        setFieldValue(`items`, items)

    }

    return (
        <Box
            sx={{
                mx: -1
            }}
        >
            {/* {JSON.stringify(values)} */}
            <RenderForm
                currentLangId={currentLangId}
                gridRef={gridRef}
                t={t}
                formik={formik}
                summaryItems={summaryItems}
                printPackQtyValue={printPackQtyValue}
                handleAddRow={({ rowCount = 1 }) => {

                    rowCount = parseInt(rowCount, 10)

                    if ((values.items.length + rowCount) > maxItemLength) {
                        promptMessage(false, `Max only ${maxItemLength}`)
                        return false
                    }

                    let newItems = [...Array(rowCount)].map((_, i) => {
                        return {
                            ...initialItemValues,
                            id: generateRowId(),
                            uomId: values.items[0].uomId,
                            code: getUniqueItemCode({ courierTrackingNo, productId: values.items[0].productId }),
                            productId: values.items[0].productId,
                            customUomName: t(initialItemValues.customUomName)
                        }
                    })

                    setFieldValue(`items`, [
                        ...values.items,
                        ...newItems
                    ])

                    promptMessage(true, t('added1NewItemEntry'))

                    return true

                }}
                handleRemoveRows={handleRemoveRows}
                onCellValueChanged={onCellValueChanged}
                allowDeleteFirstRowAfterSave={allowDeleteFirstRowAfterSave}
                onCalculateProductItem={onCalculateProductItem}
                getRoute={getRoute}
                availableLocations={availableLocations}
            />

        </Box>
    )
}